import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  ArrowRight,
  Loader2, 
  Gift,
  Users,
  Palette,
  Check,
  Calendar as CalendarIcon,
  Plus,
  Upload,
  Image as ImageIcon,
  Sparkles,
  Trophy,
  Clock,
  Info,
  Eye,
  Link2,
  Store,
  FileSpreadsheet,
  Shuffle,
  ListOrdered,
  Building2,
  AlertTriangle,
  X,
  Heart
} from "lucide-react";
import { toast } from "sonner";
import { drawsAPI } from "../lib/api";
import * as XLSX from "xlsx";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import AnimatedLogo from "../components/AnimatedLogo";
import UserMenu from "../components/UserMenu";
import { InstallAppButton } from "../components/AppHeader";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { getImageUrl } from "../lib/utils";

// SIMPLIFIED 2-STEP STRUCTURE (Lots are managed in DrawEditor after creation)
const STEPS = [
  { id: 1, title: "Configuration", icon: Info },
  { id: 2, title: "Design", icon: Palette }
];

const IMPORT_ORIGINS = [
  { value: "client", label: "Clients" },
  { value: "member", label: "Membres" },
  { value: "subscriber", label: "Abonnés" },
  { value: "other", label: "Autre" }
];

// Helper to extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return "";
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : "";
};

export default function GamifiedDrawCreator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingOrgLogo, setUploadingOrgLogo] = useState(false);
  const [uploadingBenefLogo, setUploadingBenefLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  
  // STEP 1: Configuration (Info + Mode + Dates + Acquisition)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  
  // Draw mode
  const [drawMode, setDrawMode] = useState("random"); // "random" or "registration_order"
  
  // Dates
  const [dates, setDates] = useState({
    start_date: new Date(),
    draw_date: addDays(new Date(), 7),
    participation_deadline: null,
    publication_end_date: addDays(new Date(), 14)
  });
  
  // Acquisition modes
  const [acquisitionModes, setAcquisitionModes] = useState({
    public_link: true,
    shop_mode: false,
    import_list: false
  });
  
  const [importAttestation, setImportAttestation] = useState({
    opt_in: false,
    origin: ""
  });
  
  const [selectedLists, setSelectedLists] = useState([]);
  const [customFieldLabel, setCustomFieldLabel] = useState("");
  const [importedParticipants, setImportedParticipants] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  
  // STEP 2: Design & Identity
  const [design, setDesign] = useState({
    // Organizer
    organizer_name: user?.company_name || "",
    organizer_logo: "",
    organizer_contact: user?.email || "",
    // Beneficiary
    beneficiary_name: "",
    beneficiary_logo: "",
    // Visual
    bg_type: "color",
    bg_value: "#3B82F6",
    bg_video_url: "",
    accent_color: "#EAB308",
    // Partner carousel
    partner_carousel_enabled: false,
    partner_carousel_title: "Merci à nos partenaires",
    partner_logos: []
  });
  
  // Available participant lists
  const [lists, setLists] = useState([]);
  const hasAnnualLicense = user?.subscription_type === "annual";

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    try {
      const res = await api.get("/participant-lists");
      setLists(res.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  // VALIDATION - Simplified 2 steps (Lots are managed in DrawEditor after creation)
  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast.error("Le nom du tirage est obligatoire");
        return;
      }
      if (!acquisitionModes.public_link && !acquisitionModes.shop_mode && !acquisitionModes.import_list) {
        toast.error("Sélectionnez au moins un mode d'acquisition");
        return;
      }
      if (acquisitionModes.import_list && !importAttestation.opt_in) {
        toast.error("Vous devez attester que votre base est Opt-in pour l'import");
        return;
      }
      if (acquisitionModes.import_list && !importAttestation.origin) {
        toast.error("Précisez l'origine de votre liste");
        return;
      }
    }
    
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // FILE IMPORT HANDLER
  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(fileExt)) {
      toast.error("Format non supporté. Utilisez CSV, XLS ou XLSX.");
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target.result;
        let workbook;
        
        if (fileExt === 'csv') {
          workbook = XLSX.read(data, { type: 'string' });
        } else {
          workbook = XLSX.read(data, { type: 'binary' });
        }
        
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        
        const participants = jsonData.map(row => ({
          name: row.nom || row.name || row.Nom || row.Name || "",
          email: row.email || row.Email || row.EMAIL || "",
          phone: row.phone || row.telephone || row.Phone || row.Telephone || ""
        })).filter(p => p.name || p.email);
        
        setImportedParticipants(participants);
        setImportFileName(file.name);
        toast.success(`${participants.length} participants importés`);
      };
      
      if (fileExt === 'csv') {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    } catch (error) {
      toast.error("Erreur lors de l'import du fichier");
    }
  };

  // UPLOAD HANDLERS FOR DESIGN
  const handleUploadOrgLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOrgLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const res = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDesign({ ...design, organizer_logo: res.data.url });
      toast.success("Logo organisateur uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingOrgLogo(false);
    }
  };

  const handleUploadBenefLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBenefLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const res = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDesign({ ...design, beneficiary_logo: res.data.url });
      toast.success("Logo bénéficiaire uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingBenefLogo(false);
    }
  };

  const handleUploadBg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const res = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDesign({ ...design, bg_type: "image", bg_value: res.data.url });
      toast.success("Image de fond uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingBg(false);
    }
  };

  const handleUploadPartnerLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const res = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const newLogos = [...(design.partner_logos || []), { url: res.data.url, name: file.name.split('.')[0] }];
      setDesign({ ...design, partner_logos: newLogos });
      toast.success("Logo partenaire ajouté");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
    e.target.value = '';
  };

  // SUBMIT - Create draw without prizes (prizes managed in DrawEditor)
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const drawData = {
        title: formData.title,
        description: formData.description,
        prize_attribution_mode: drawMode, // Backend expects 'prize_attribution_mode'
        start_date: dates.start_date?.toISOString(),
        draw_date: dates.draw_date?.toISOString(),
        participation_deadline: dates.participation_deadline?.toISOString(),
        publication_end_date: dates.publication_end_date?.toISOString(),
        acquisition_modes: acquisitionModes,
        custom_field_label: customFieldLabel,
        prizes: [], // Lots will be added in DrawEditor
        // Design fields
        organizer_name: design.organizer_name,
        organizer_logo: design.organizer_logo,
        organizer_contact: design.organizer_contact,
        beneficiary_name: design.beneficiary_name,
        beneficiary_logo: design.beneficiary_logo,
        background_type: design.bg_type,
        background_color: design.bg_type === "color" ? design.bg_value : null,
        background_image: design.bg_type === "image" ? design.bg_value : null,
        background_gradient: design.bg_type === "gradient" ? design.bg_value : null,
        background_video_url: design.bg_type === "video" ? design.bg_video_url : null,
        accent_color: design.accent_color,
        partner_carousel_enabled: design.partner_carousel_enabled,
        partner_carousel_title: design.partner_carousel_title,
        partner_logos: design.partner_logos,
        status: "draft"
      };

      const res = await drawsAPI.create(drawData);
      
      // Import participants if any
      if (importedParticipants.length > 0) {
        await api.post(`/draws/${res.data.id}/import-participants`, {
          participants: importedParticipants,
          origin: importAttestation.origin
        });
      }

      toast.success("Tirage créé ! Ajoutez maintenant vos lots dans l'éditeur.");
      navigate(`/draws/${res.data.id}/edit`);
    } catch (error) {
      console.error("Error creating draw:", error);
      toast.error("Erreur lors de la création du tirage");
    } finally {
      setLoading(false);
    }
  };

  // Preview background style
  const getPreviewBgStyle = () => {
    if (design.bg_type === "video" && design.bg_video_url) {
      return { backgroundColor: "#000" };
    }
    if (design.bg_type === "image" && design.bg_value) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${getImageUrl(design.bg_value)})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    }
    if (design.bg_type === "gradient" && design.bg_value) {
      return { background: design.bg_value };
    }
    return { backgroundColor: design.bg_value || "#3B82F6" };
  };

  const progress = Math.round((currentStep / 2) * 100);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-muted rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <AnimatedLogo />
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Title & Progress */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              Créer un tirage au sort
            </h1>
            <p className="text-muted-foreground">
              Étape {currentStep} sur 2 : {STEPS[currentStep - 1].title}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-full">
            <span className="text-2xl font-bold text-primary">{progress}%</span>
          </div>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isCompleted ? "bg-green-500 text-white" :
                    isActive ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                  </div>
                  <span className={`text-sm mt-2 ${isActive ? "text-primary font-medium" : "text-muted-foreground"}`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${isCompleted ? "bg-green-500" : "bg-muted"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* ============ STEP 1: CONFIGURATION ============ */}
        {currentStep === 1 && (
          <Card className="bg-card">
            <CardContent className="p-6 space-y-8">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  Informations du tirage
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom du tirage *</Label>
                    <Input
                      placeholder="Ex: Grand Tirage de Noël 2026"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-background h-12 text-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Décrivez votre tirage au sort..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-background min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              {/* Draw Mode */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shuffle className="w-5 h-5 text-primary" />
                  Mode de tirage
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setDrawMode("random")}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      drawMode === "random" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <Shuffle className="w-6 h-6 text-primary mb-2" />
                    <p className="font-medium">Tirage au hasard</p>
                    <p className="text-sm text-muted-foreground">Les gagnants sont tirés aléatoirement</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDrawMode("registration_order")}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      drawMode === "registration_order" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <ListOrdered className="w-6 h-6 text-primary mb-2" />
                    <p className="font-medium">Par ordre des lots</p>
                    <p className="text-sm text-muted-foreground">1er tiré = Lot n°1, 2ème tiré = Lot n°2...</p>
                  </button>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Dates de l'opération
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      Début des inscriptions
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start bg-background">
                            {dates.start_date ? format(dates.start_date, "dd MMM yyyy", { locale: fr }) : "Date..."}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dates.start_date}
                            onSelect={(date) => {
                              if (date && dates.start_date) {
                                date.setHours(dates.start_date.getHours(), dates.start_date.getMinutes());
                              }
                              setDates({ ...dates, start_date: date });
                            }}
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={dates.start_date ? format(dates.start_date, "HH:mm") : "09:00"}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':').map(Number);
                          const newDate = dates.start_date ? new Date(dates.start_date) : new Date();
                          newDate.setHours(hours, minutes);
                          setDates({ ...dates, start_date: newDate });
                        }}
                        className="w-24 bg-background"
                      />
                    </div>
                  </div>
                  
                  {/* Draw Date */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Trophy className="w-4 h-4" />
                      Date du tirage
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start bg-background">
                            {dates.draw_date ? format(dates.draw_date, "dd MMM yyyy", { locale: fr }) : "Date..."}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dates.draw_date}
                            onSelect={(date) => {
                              if (date && dates.draw_date) {
                                date.setHours(dates.draw_date.getHours(), dates.draw_date.getMinutes());
                              }
                              const pubEndDate = date ? addDays(date, 7) : dates.publication_end_date;
                              setDates({ ...dates, draw_date: date, publication_end_date: pubEndDate });
                            }}
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={dates.draw_date ? format(dates.draw_date, "HH:mm") : "18:00"}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':').map(Number);
                          const newDate = dates.draw_date ? new Date(dates.draw_date) : new Date();
                          newDate.setHours(hours, minutes);
                          setDates({ ...dates, draw_date: newDate });
                        }}
                        className="w-24 bg-background"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Acquisition Modes */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Sources de participation
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Public Link */}
                  <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    acquisitionModes.public_link ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  }`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={acquisitionModes.public_link}
                        onCheckedChange={(checked) => setAcquisitionModes({ ...acquisitionModes, public_link: checked })}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-primary" />
                          <span className="font-medium">Lien public</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Page d'inscription en ligne</p>
                      </div>
                    </div>
                  </label>

                  {/* Shop Mode */}
                  <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    acquisitionModes.shop_mode ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  }`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={acquisitionModes.shop_mode}
                        onCheckedChange={(checked) => setAcquisitionModes({ ...acquisitionModes, shop_mode: checked })}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-primary" />
                          <span className="font-medium">Mode boutique</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Inscription rapide en magasin</p>
                      </div>
                    </div>
                  </label>

                  {/* Import List */}
                  <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    acquisitionModes.import_list ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  }`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={acquisitionModes.import_list}
                        onCheckedChange={(checked) => setAcquisitionModes({ ...acquisitionModes, import_list: checked })}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-primary" />
                          <span className="font-medium">Import liste</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Importer depuis Excel/CSV</p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Import Attestation */}
                {acquisitionModes.import_list && (
                  <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-800 dark:text-amber-200">Attestation Opt-in obligatoire</h4>
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            Pour importer une liste, vous devez attester que les personnes ont donné leur consentement.
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox
                            checked={importAttestation.opt_in}
                            onCheckedChange={(checked) => setImportAttestation({ ...importAttestation, opt_in: checked })}
                            className="mt-0.5"
                          />
                          <span className="text-sm">
                            J'atteste que ma base de données est <strong>Opt-in</strong> et que les personnes ont consenti à recevoir des communications.
                          </span>
                        </label>
                        
                        <div className="space-y-2">
                          <Label className="text-sm">Origine de la liste *</Label>
                          <Select
                            value={importAttestation.origin}
                            onValueChange={(value) => setImportAttestation({ ...importAttestation, origin: value })}
                          >
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Sélectionnez l'origine..." />
                            </SelectTrigger>
                            <SelectContent>
                              {IMPORT_ORIGINS.map((origin) => (
                                <SelectItem key={origin.value} value={origin.value}>{origin.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* File upload */}
                        <div className="space-y-2 pt-2 border-t border-amber-300/50">
                          <Label className="text-sm">Importer un fichier CSV/Excel</Label>
                          <Input
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileImport}
                            className="bg-background text-sm"
                          />
                          {importedParticipants.length > 0 && (
                            <div className="flex items-center gap-2 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                              <FileSpreadsheet className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-700 dark:text-green-300">
                                {importedParticipants.length} participants importés
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Custom field */}
                <div className="space-y-2">
                  <Label className="text-sm">Champ personnalisé (optionnel)</Label>
                  <Input
                    placeholder="Ex: Numéro de ticket, N° de commande..."
                    value={customFieldLabel}
                    onChange={(e) => setCustomFieldLabel(e.target.value)}
                    className="bg-background"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============ STEP 2: DESIGN & IDENTITY ============ */}
        {currentStep === 2 && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Controls */}
            <Card className="bg-card">
              <CardContent className="p-6 space-y-6">
                {/* Organizer Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Organisateur
                  </h3>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'organisateur</Label>
                      <Input
                        placeholder="Votre entreprise"
                        value={design.organizer_name}
                        onChange={(e) => setDesign({ ...design, organizer_name: e.target.value })}
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Logo de l'organisateur</Label>
                      <div className="flex items-center gap-3">
                        <input type="file" id="org-logo" className="hidden" accept="image/*" onChange={handleUploadOrgLogo} />
                        {design.organizer_logo ? (
                          <div className="relative group">
                            <img src={getImageUrl(design.organizer_logo)} alt="Logo" className="h-16 w-16 object-contain rounded-lg bg-white p-2 border" />
                            <button onClick={() => setDesign({ ...design, organizer_logo: "" })} className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="org-logo" className="h-16 px-4 flex items-center gap-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50">
                            {uploadingOrgLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 text-muted-foreground" /><span className="text-sm text-muted-foreground">Uploader</span></>}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Beneficiary Section */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Heart className="w-5 h-5 text-pink-500" />
                    Au profit de... (optionnel)
                  </h3>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label>Nom du bénéficiaire</Label>
                      <Input
                        placeholder="Ex: Association XYZ"
                        value={design.beneficiary_name}
                        onChange={(e) => setDesign({ ...design, beneficiary_name: e.target.value })}
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Logo du bénéficiaire</Label>
                      <div className="flex items-center gap-3">
                        <input type="file" id="benef-logo" className="hidden" accept="image/*" onChange={handleUploadBenefLogo} />
                        {design.beneficiary_logo ? (
                          <div className="relative group">
                            <img src={getImageUrl(design.beneficiary_logo)} alt="Logo" className="h-16 w-16 object-contain rounded-lg bg-white p-2 border" />
                            <button onClick={() => setDesign({ ...design, beneficiary_logo: "" })} className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label htmlFor="benef-logo" className="h-16 px-4 flex items-center gap-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50">
                            {uploadingBenefLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 text-muted-foreground" /><span className="text-sm text-muted-foreground">Uploader</span></>}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Background Section */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Palette className="w-5 h-5 text-primary" />
                    Fond de page
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: "color", label: "Couleur" },
                      { value: "gradient", label: "Dégradé" },
                      { value: "image", label: "Image" },
                      { value: "video", label: "Vidéo" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setDesign({ ...design, bg_type: option.value })}
                        className={`p-2 rounded-lg border text-sm transition-all ${
                          design.bg_type === option.value ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  
                  {design.bg_type === "color" && (
                    <div className="flex items-center gap-3">
                      <input type="color" value={design.bg_value} onChange={(e) => setDesign({ ...design, bg_value: e.target.value })} className="w-12 h-12 rounded-lg cursor-pointer border-0" />
                      <Input value={design.bg_value} onChange={(e) => setDesign({ ...design, bg_value: e.target.value })} className="bg-background w-28 font-mono" />
                    </div>
                  )}
                  
                  {design.bg_type === "gradient" && (
                    <div className="grid grid-cols-6 gap-2">
                      {[
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                        "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                        "linear-gradient(135deg, #30cfd0 0%, #330867 100%)"
                      ].map((gradient, i) => (
                        <button key={i} onClick={() => setDesign({ ...design, bg_value: gradient })} className={`w-full aspect-square rounded-lg border-2 transition-all ${design.bg_value === gradient ? "border-primary scale-110" : "border-transparent"}`} style={{ background: gradient }} />
                      ))}
                    </div>
                  )}
                  
                  {design.bg_type === "image" && (
                    <div className="flex items-center gap-4">
                      <input type="file" id="bg-upload" className="hidden" accept="image/*" onChange={handleUploadBg} />
                      {design.bg_value && design.bg_type === "image" ? (
                        <div className="relative group">
                          <img src={getImageUrl(design.bg_value)} alt="Background" className="h-20 w-32 object-cover rounded-lg" />
                          <button onClick={() => setDesign({ ...design, bg_value: "#3B82F6", bg_type: "color" })} className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label htmlFor="bg-upload" className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50">
                          {uploadingBg ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImageIcon className="w-5 h-5 text-muted-foreground" /><span className="text-sm text-muted-foreground">Uploader une image</span></>}
                        </label>
                      )}
                    </div>
                  )}
                  
                  {design.bg_type === "video" && (
                    <div className="space-y-3">
                      <Input
                        placeholder="URL YouTube (ex: https://youtu.be/xxxxx)"
                        value={design.bg_video_url || ""}
                        onChange={(e) => setDesign({ ...design, bg_video_url: e.target.value })}
                        className="bg-background"
                      />
                      <p className="text-xs text-muted-foreground">La vidéo sera en arrière-plan avec un overlay noir à 70%</p>
                      {design.bg_video_url && (
                        <div className="relative h-24 rounded-lg overflow-hidden bg-black">
                          <div className="absolute inset-0 bg-black/70 z-10" />
                          <iframe src={`https://www.youtube.com/embed/${extractYouTubeId(design.bg_video_url)}?autoplay=0&mute=1`} className="w-full h-full" frameBorder="0" />
                          <p className="absolute inset-0 z-20 flex items-center justify-center text-white text-xs">Aperçu avec overlay 70%</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Accent Color */}
                <div className="space-y-3 pt-4 border-t">
                  <Label>Couleur d'accent (boutons)</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={design.accent_color} onChange={(e) => setDesign({ ...design, accent_color: e.target.value })} className="w-12 h-12 rounded-lg cursor-pointer border-0" />
                    <Input value={design.accent_color} onChange={(e) => setDesign({ ...design, accent_color: e.target.value })} className="bg-background w-28 font-mono" />
                  </div>
                </div>

                {/* Partner Carousel */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Carrousel de partenaires
                    </Label>
                    <Switch checked={design.partner_carousel_enabled} onCheckedChange={(checked) => setDesign({ ...design, partner_carousel_enabled: checked })} />
                  </div>
                  
                  {design.partner_carousel_enabled && (
                    <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-sm">Titre du bandeau</Label>
                        <Input value={design.partner_carousel_title} onChange={(e) => setDesign({ ...design, partner_carousel_title: e.target.value })} className="bg-background" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Logos des partenaires</Label>
                        <div className="flex flex-wrap gap-3">
                          {(design.partner_logos || []).map((logo, index) => (
                            <div key={index} className="relative group">
                              <img src={getImageUrl(logo.url)} alt={logo.name} className="h-14 w-14 object-contain rounded-lg bg-white p-1 border" />
                              <button onClick={() => {
                                const newLogos = [...design.partner_logos];
                                newLogos.splice(index, 1);
                                setDesign({ ...design, partner_logos: newLogos });
                              }} className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <input type="file" id="partner-upload" className="hidden" accept="image/*" onChange={handleUploadPartnerLogo} />
                          <label htmlFor="partner-upload" className="h-14 w-14 flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50">
                            <Plus className="w-5 h-5 text-muted-foreground" />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Right: Preview */}
            <Card className="bg-card sticky top-24">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Aperçu en temps réel
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="rounded-xl overflow-hidden relative" style={{ ...getPreviewBgStyle(), minHeight: "400px" }}>
                  {/* Video background */}
                  {design.bg_type === "video" && design.bg_video_url && (
                    <>
                      <div className="absolute inset-0 bg-black/70 z-10" />
                      <iframe
                        src={`https://www.youtube.com/embed/${extractYouTubeId(design.bg_video_url)}?autoplay=1&mute=1&loop=1&controls=0&playlist=${extractYouTubeId(design.bg_video_url)}`}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="autoplay"
                      />
                    </>
                  )}
                  
                  <div className="relative z-20 p-6 text-center text-white">
                    {design.organizer_logo && (
                      <img src={getImageUrl(design.organizer_logo)} alt="Logo" className="h-12 mx-auto mb-4 bg-white rounded-lg p-2" />
                    )}
                    <h2 className="text-2xl font-bold mb-2">{formData.title || "Nom du tirage"}</h2>
                    <p className="text-sm opacity-80 mb-4">{design.organizer_name || "Organisateur"}</p>
                    
                    <div className="mb-4">
                      <p className="text-sm opacity-70 mb-2 flex items-center justify-center gap-2">
                        <Gift className="w-4 h-4" />
                        Les lots seront ajoutés après la création
                      </p>
                    </div>
                    
                    <Button className="rounded-full" style={{ backgroundColor: design.accent_color }}>
                      Participer au tirage
                    </Button>
                    
                    {design.beneficiary_name && (
                      <div className="mt-4 pt-4 border-t border-white/20">
                        <p className="text-xs opacity-70 flex items-center justify-center gap-1">
                          <Heart className="w-3 h-3" /> Au profit de {design.beneficiary_name}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs opacity-50 mt-6">Propulsé par TIREO</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>
          
          {currentStep < 2 ? (
            <Button onClick={handleNext} className="rounded-full">
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Créer le tirage
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
